import type { GetMessagesQueryVariables } from '#src/graphql/generated';
import type { EditorJsData } from './editorjs-types';

import { useApolloClient, useMutation } from '@apollo/client/react';
import {
  Popover,
  Portal,
  Button,
  Spinner,
  VStack,
  Textarea,
  Text,
  HStack,
} from '@chakra-ui/react';
import React from 'react';
import { FiZap } from 'react-icons/fi';

import { ChatComposerRow } from '#src/components/chat-composer-row';
import { toaster } from '#src/components/ui/toaster';
import { ASK_AI_REACT_PROCESSOR_ID } from '#src/constants/processors';
import { mergeCreateMessageIntoGetMessagesCache } from '#src/graphql/cache/merge-create-message-into-get-messages';
import { CREATE_MESSAGE } from '#src/graphql/mutations/create-message';
import { QUEUE_EXECUTE_PROCESSOR } from '#src/graphql/mutations/queue-execute-processor';
import { persistProcessorFailureAsDocumentRootMessage } from '#src/utils/persist-processor-failure-message';

export interface PromptCommandBaseProps {
  /** Document ID - required for processing prompts */
  documentId?: string;
  /** Whether this is a new document (not yet saved) */
  isNewDocument: boolean;
  /** Optional tuned block id to scope the AI request. */
  focusBlockId?: string | null;
  /** Callback to set the editor value with the AI-edited document (user confirms and saves manually) */
  onValueUpdate: (value: EditorJsData | null) => void;
  /** Current editor JSON string so the server can apply edits on unsaved changes. */
  getEditorSnapshot?: () => string | null;
  /** OpenAI chat model id for this request; must be in the deployment allowlist. */
  model?: string;
  /**
   * Document message thread root to append this edit to in the chat log (server
   * `recordDocumentAiEditMessages`). Omit to start a new thread.
   */
  threadRootId?: string | null;
  /**
   * Registers the user message created for this edit; completion is driven by
   * `documentMessagesUpdated` + message query refetch (see `useDocumentAiEditCompletion`).
   */
  registerPendingDocumentAiEdit?: (
    userMessageId: string,
    threadRootId: string | undefined,
  ) => void;
  /**
   * Fires after the user message exists and the ask-ai-react run is queued successfully.
   * `threadRootId` is the thread root for UI (new root id when starting a thread).
   */
  onEditProcessorQueued?: (threadRootId: string | undefined) => void;
}

export function usePromptSubmit({
  documentId,
  isNewDocument,
  focusBlockId,
  getEditorSnapshot,
  model,
  threadRootId,
  registerPendingDocumentAiEdit,
  onEditProcessorQueued,
}: PromptCommandBaseProps) {
  const apolloClient = useApolloClient();
  const [createMessage] = useMutation(CREATE_MESSAGE);
  const [queueExecuteProcessor] = useMutation(QUEUE_EXECUTE_PROCESSOR);
  const [processingPrompt, setProcessingPrompt] = React.useState(false);

  const documentMessagesVariables = React.useMemo<GetMessagesQueryVariables>(
    () => ({
      input: {
        contextFilters: [{ name: 'documentId', value: documentId ?? '' }],
      },
    }),
    [documentId],
  );

  const disabled = processingPrompt || isNewDocument;

  const handleSubmit = React.useCallback(
    async (promptText: string): Promise<boolean> => {
      if (!promptText.trim()) return false;
      if (!documentId) {
        toaster.create({
          title: 'Error',
          description: 'Save the document first to use AI modifications.',
          type: 'error',
        });
        return false;
      }

      setProcessingPrompt(true);
      try {
        const snap = getEditorSnapshot?.() ?? null;
        const parentThreadRootId = threadRootId?.trim() || undefined;

        /** New document threads need `documentId` on the root message for scoping. */
        const contexts: { name: string; value: string }[] = [];
        if (!parentThreadRootId) {
          contexts.push({ name: 'documentId', value: documentId });
        }

        const { data: createData } = await createMessage({
          variables: {
            input: {
              value: promptText.trim(),
              ...(parentThreadRootId
                ? { parentMessageId: parentThreadRootId }
                : {}),
              ...(contexts.length > 0 ? { contexts } : {}),
            },
          },
          update(cache, { data: mutationData }) {
            if (mutationData?.createMessage) {
              mergeCreateMessageIntoGetMessagesCache(
                cache,
                mutationData.createMessage,
                documentMessagesVariables,
              );
            }
          },
        });

        const created = createData?.createMessage;
        if (!created?.id) {
          toaster.create({
            title: 'Could not start AI edit',
            description: 'Create message returned no id.',
            type: 'error',
          });
          return false;
        }

        const { data: queueData } = await queueExecuteProcessor({
          variables: {
            input: {
              processorId: ASK_AI_REACT_PROCESSOR_ID,
              variables: {
                input: promptText.trim(),
                documentId,
                reuseUserMessageId: created.id,
                ...(parentThreadRootId
                  ? { threadRootId: parentThreadRootId }
                  : {}),
                ...(model?.trim() ? { model: model.trim() } : {}),
                ...(snap && snap.length > 0
                  ? { currentEditorValue: snap }
                  : {}),
                ...(focusBlockId?.trim()
                  ? { focusBlockId: focusBlockId.trim() }
                  : {}),
              },
            },
          },
        });

        const q = queueData?.queueExecuteProcessor;
        if (!q?.queued) {
          toaster.create({
            title: 'Could not queue AI edit',
            description: q?.message ?? 'Unknown error',
            type: 'error',
          });
          return false;
        }

        const effectiveThreadRootId = parentThreadRootId ?? created.id;

        onEditProcessorQueued?.(effectiveThreadRootId);
        registerPendingDocumentAiEdit?.(created.id, effectiveThreadRootId);

        return true;
      } catch (error) {
        const description =
          error instanceof Error ? error.message : 'Unknown error';
        if (documentId) {
          const persist = await persistProcessorFailureAsDocumentRootMessage(
            apolloClient,
            {
              documentId,
              errorMessage: description,
              processorInstanceId: undefined,
              getMessagesVariables: documentMessagesVariables,
            },
          );
          if (!persist.ok) {
            toaster.create({
              title: 'Could not save error to messages',
              description: persist.error,
              type: 'warning',
            });
          }
        } else {
          toaster.create({
            title: 'Error',
            description: `Error applying AI edit: ${description}`,
            type: 'error',
          });
        }
        return false;
      } finally {
        setProcessingPrompt(false);
      }
    },
    [
      apolloClient,
      documentId,
      documentMessagesVariables,
      focusBlockId,
      model,
      threadRootId,
      getEditorSnapshot,
      createMessage,
      queueExecuteProcessor,
      onEditProcessorQueued,
      registerPendingDocumentAiEdit,
    ],
  );

  return { handleSubmit, disabled, processingPrompt };
}

/**
 * Inline AI prompt input for the document page footer (textarea + Apply).
 */
export function PromptFooterInput(props: PromptCommandBaseProps) {
  const { isNewDocument } = props;
  const [prompt, setPrompt] = React.useState('');
  const { handleSubmit, disabled, processingPrompt } = usePromptSubmit(props);

  const onSubmit = async () => {
    const text = prompt.trim();
    if (!text) return;
    setPrompt('');
    await handleSubmit(text);
  };

  return (
    <ChatComposerRow
      value={prompt}
      onChange={setPrompt}
      onSubmit={onSubmit}
      placeholder={
        isNewDocument
          ? 'Save the document first to use AI modifications'
          : 'e.g., Summarize this content, Translate to Spanish, Fix grammar errors...'
      }
      disabled={disabled}
      loading={processingPrompt}
    />
  );
}

/**
 * Prompt button (popover) for use where inline footer is not shown.
 */
export function PromptButton(props: PromptCommandBaseProps) {
  const { isNewDocument } = props;
  const [prompt, setPrompt] = React.useState('');
  const [isOpen, setIsOpen] = React.useState(false);
  const { handleSubmit, disabled, processingPrompt } = usePromptSubmit(props);

  const title = isNewDocument
    ? 'Save the document first to use AI modifications'
    : undefined;

  const onPopoverSubmit = async () => {
    if (!prompt.trim()) return;
    const promptText = prompt.trim();
    setPrompt('');
    setIsOpen(false);
    await handleSubmit(promptText);
  };

  const handleCancel = () => {
    setPrompt('');
    setIsOpen(false);
  };

  return (
    <Popover.Root
      open={isOpen}
      onOpenChange={(e) => setIsOpen(e.open)}
      positioning={{ placement: 'bottom-start' }}
      size="sm"
    >
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label="AI prompt to edit document"
          title={title ?? 'Use AI to edit this document'}
          disabled={disabled}
          onClick={() => setIsOpen(true)}
        >
          {processingPrompt ? (
            <Spinner size="sm" />
          ) : (
            <FiZap style={{ width: '24px', height: '24px' }} />
          )}
        </button>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content
            maxW="400px"
            p={4}
            bg="bg"
            border="1px solid"
            borderColor="border"
            shadow="lg"
            borderRadius="md"
          >
            <Popover.Arrow>
              <Popover.ArrowTip />
            </Popover.Arrow>
            <VStack gap={3} align="stretch">
              <Text fontSize="sm" fontWeight="semibold" color="fg">
                AI Prompt
              </Text>
              <Text fontSize="xs" color="fg.muted">
                Provide instructions for how AI should modify this document
              </Text>
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="e.g., Summarize this content, Translate to Spanish, Fix grammar errors..."
                rows={4}
                resize="vertical"
                minH="100px"
              />
              <HStack gap={2} justify="flex-end">
                <Button size="sm" variant="ghost" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  colorScheme="blue"
                  onClick={onPopoverSubmit}
                  disabled={!prompt.trim()}
                >
                  Apply
                </Button>
              </HStack>
            </VStack>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
}
